import { endIdentityTrace } from "@/lib/wallet/identity-trace";
import {
  identityAttempt,
  identityFailure,
  identityPhase,
  identityProvider,
  identityResponse,
} from "@/lib/wallet/identity-diagnostics";

import type { WalletTelemetryInput } from "@mudwater/sdk";
import type { IdentityAttempt } from "@/lib/wallet/identity-diagnostics";
import type { SignIn, SignInInput } from "@/lib/wallet/phantom-sign-in";

const instrumented = new WeakMap<
  SignIn,
  { sign: SignIn; wallet: WalletTelemetryInput }
>();

/** Prompt result only; SIWS messages, signatures and account answers stay private. */
export function instrumentSignIn(
  sign: SignIn,
  wallet: WalletTelemetryInput
): SignIn {
  const wrapped: SignIn = (input) => invokeSignIn(wrapped, input);
  instrumented.set(wrapped, { sign, wallet });
  return wrapped;
}

/** One invocation context can also join the challenge and proof-recording stages. */
export async function invokeSignIn(
  sign: SignIn,
  input: SignInInput,
  supplied?: IdentityAttempt
): Promise<unknown> {
  const known = instrumented.get(sign);
  const attempt = supplied ?? identityAttempt("wallet");
  identityProvider(attempt, known?.wallet);
  const started = performance.now();
  const elapsed = () => String(Math.round(performance.now() - started));
  identityPhase(attempt, "prompt", { ms: "0" });
  try {
    const result = await (known?.sign ?? sign)(input);
    identityPhase(attempt, "returned", {
      ms: elapsed(),
      ...identityResponse(result),
    });
    return result;
  } catch (error) {
    identityPhase(attempt, "failed", {
      ms: elapsed(),
      ...identityFailure(error),
    });
    throw error;
  } finally {
    if (!supplied) endIdentityTrace(attempt);
  }
}
